/**
 * Eliminação matemática dos prêmios (1º e 2º) — Copa 2026.
 *
 * Usa a mesma pontuação das regras (copaCalculo + PONTUACAO_POR_FASE):
 * - Grupos: até 3 pts por jogo pendente (placar exato)
 * - Mata-mata: placarExato da fase (6, 9, 12, 15 ou 18)
 *
 * Classificação por participante (melhor cenário nos jogos sem resultado):
 * - Todos os jogos pendentes entram no teto (placar exato), mesmo sem palpite
 *   preenchido — o participante ainda pode pontuar enquanto o jogo não saiu.
 * - disputa_1: ainda pode alcançar ou empatar o líder
 * - so_2: não alcança o 1º, mas ainda pode ficar no 2º lugar premiado
 * - sem_premio: nem no melhor cenário alcança a barreira mínima de prêmio
 *
 * Barreira de prêmio: pontuação do 2º colocado atual; se houver empate
 * no topo ocupando 1º+2º, só quem alcançar o líder ainda pode premiar.
 */
import { JOGOS_COPA_2026 } from '../data/copaMundo2026';
import {
  JOGOS_MATA_MATA,
  PONTUACAO_POR_FASE,
  type FaseMataMata,
} from '../data/mataMataCopa2026';
import {
  placarValido,
  pontuacaoParticipante,
  type Placar,
  type PlacaresMap,
  type VencedoresPenaltisMMMap,
} from './copaCalculo';

export type StatusPremioMatematico = 'disputa_1' | 'so_2' | 'sem_premio';

export interface ParticipanteEliminacao {
  id: string;
  nome: string;
  pontosAtuais: number;
  maxPossivelFinal: number;
  pontosRestantesMax: number;
  jogosPendentesComPalpite: number;
  status: {
    codigo: StatusPremioMatematico;
    /** Texto curto para exibição na UI */
    rotulo: string;
  };
}

export interface EliminacaoMatematicaCopa {
  pontosLider: number;
  pontosBarreiraPremio: number;
  nomesLideres: string[];
  /** Quem está na barreira mínima para ainda receber prêmio (2º colocado ou líderes em empate no topo) */
  nomesBarreiraPremio: string[];
  /** Empate no topo ocupa as duas vagas premiadas — explicação na UI */
  empateNoTopoOcupaPremios: boolean;
  jogosSemResultado: number;
  disputaPrimeiro: ParticipanteEliminacao[];
  soSegundo: ParticipanteEliminacao[];
  semPremio: ParticipanteEliminacao[];
}

interface JogoPendente {
  id: string;
  ehMataMata: boolean;
  fase?: FaseMataMata;
}

/** Melhor cenário por jogo ainda sem resultado (placar exato nas regras do bolão). */
function maxPontosJogoPendente(ehMataMata: boolean, fase?: FaseMataMata): number {
  if (!ehMataMata) return 3;
  if (!fase) return 0;
  return PONTUACAO_POR_FASE[fase].placarExato;
}

/** Piso teórico por jogo (só acerto do vencedor) — referência para UI. */
export function minPontosJogoPendente(ehMataMata: boolean, fase?: FaseMataMata): number {
  if (!ehMataMata) return 1;
  if (!fase) return 0;
  return PONTUACAO_POR_FASE[fase].acertoVencedor;
}

/** Soma dos tetos de todos os jogos ainda sem resultado oficial. */
export function totalMaxPontosJogosPendentes(reais: PlacaresMap): number {
  return listarJogosPendentes(reais).reduce(
    (s, j) => s + maxPontosJogoPendente(j.ehMataMata, j.fase),
    0
  );
}

function listarJogosPendentes(reais: PlacaresMap): JogoPendente[] {
  const pendentes: JogoPendente[] = [];
  for (const j of JOGOS_COPA_2026) {
    if (!placarValido(reais[j.id])) {
      pendentes.push({ id: j.id, ehMataMata: false });
    }
  }
  for (const j of JOGOS_MATA_MATA) {
    if (!placarValido(reais[j.id])) {
      pendentes.push({ id: j.id, ehMataMata: true, fase: j.fase });
    }
  }
  return pendentes;
}

function calcularBarreirasPremio(
  linhas: { pontosAtuais: number; nome: string }[]
): {
  pontosLider: number;
  pontosBarreiraPremio: number;
  nomesLideres: string[];
  nomesBarreiraPremio: string[];
  empateNoTopoOcupaPremios: boolean;
} {
  if (linhas.length === 0) {
    return {
      pontosLider: 0,
      pontosBarreiraPremio: 0,
      nomesLideres: [],
      nomesBarreiraPremio: [],
      empateNoTopoOcupaPremios: false,
    };
  }

  const pontosUnicos = [...new Set(linhas.map((l) => l.pontosAtuais))].sort((a, b) => b - a);
  const pontosLider = pontosUnicos[0];
  const nomesLideres = linhas
    .filter((l) => l.pontosAtuais === pontosLider)
    .map((l) => l.nome)
    .sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const empateNoTopoOcupaPremios = nomesLideres.length >= 2;
  const pontosBarreiraPremio = empateNoTopoOcupaPremios
    ? pontosLider
    : pontosUnicos.length >= 2
      ? pontosUnicos[1]
      : pontosLider;

  const nomesBarreiraPremio =
    pontosBarreiraPremio === pontosLider
      ? [...nomesLideres]
      : linhas
          .filter((l) => l.pontosAtuais === pontosBarreiraPremio)
          .map((l) => l.nome)
          .sort((a, b) => a.localeCompare(b, 'pt-BR'));

  return {
    pontosLider,
    pontosBarreiraPremio,
    nomesLideres,
    nomesBarreiraPremio,
    empateNoTopoOcupaPremios,
  };
}

function classificarStatus(
  maxPossivelFinal: number,
  pontosLider: number,
  pontosBarreiraPremio: number
): ParticipanteEliminacao['status'] {
  if (maxPossivelFinal >= pontosLider) {
    return { codigo: 'disputa_1', rotulo: 'Ainda disputa o 1º lugar' };
  }
  if (maxPossivelFinal >= pontosBarreiraPremio) {
    return { codigo: 'so_2', rotulo: 'Só pode ser 2º — sem chance de 1º' };
  }
  return { codigo: 'sem_premio', rotulo: 'Sem chance de prêmio' };
}

function ordenarPorMaxELNome(a: ParticipanteEliminacao, b: ParticipanteEliminacao): number {
  return (
    a.maxPossivelFinal - b.maxPossivelFinal ||
    a.nome.localeCompare(b.nome, 'pt-BR')
  );
}

export function mapaStatusEliminacaoPorId(
  elim: EliminacaoMatematicaCopa
): Map<string, StatusPremioMatematico> {
  const m = new Map<string, StatusPremioMatematico>();
  for (const p of elim.disputaPrimeiro) m.set(p.id, 'disputa_1');
  for (const p of elim.soSegundo) m.set(p.id, 'so_2');
  for (const p of elim.semPremio) m.set(p.id, 'sem_premio');
  return m;
}

export function calcularEliminacaoMatematica(
  participantes: { id: string; nome: string; palpites: PlacaresMap }[],
  resultadosReais: PlacaresMap,
  vencedoresPenaltisMM: VencedoresPenaltisMMMap = {}
): EliminacaoMatematicaCopa {
  const jogosPendentes = listarJogosPendentes(resultadosReais);

  const linhasBase = participantes.map((p) => {
    const calc = pontuacaoParticipante(
      p.palpites,
      resultadosReais,
      JOGOS_COPA_2026,
      JOGOS_MATA_MATA,
      vencedoresPenaltisMM
    );

    let pontosRestantesMax = 0;
    let jogosPendentesComPalpite = 0;
    for (const j of jogosPendentes) {
      pontosRestantesMax += maxPontosJogoPendente(j.ehMataMata, j.fase);
      if (placarValido(p.palpites[j.id])) {
        jogosPendentesComPalpite += 1;
      }
    }

    return {
      id: p.id,
      nome: p.nome,
      pontosAtuais: calc.total,
      maxPossivelFinal: calc.total + pontosRestantesMax,
      pontosRestantesMax,
      jogosPendentesComPalpite,
    };
  });

  const barreiras = calcularBarreirasPremio(linhasBase);

  const linhas: ParticipanteEliminacao[] = linhasBase.map((l) => ({
    ...l,
    status: classificarStatus(
      l.maxPossivelFinal,
      barreiras.pontosLider,
      barreiras.pontosBarreiraPremio
    ),
  }));

  const disputaPrimeiro = linhas
    .filter((l) => l.status.codigo === 'disputa_1')
    .sort(
      (a, b) =>
        b.maxPossivelFinal - a.maxPossivelFinal ||
        a.nome.localeCompare(b.nome, 'pt-BR')
    );

  const soSegundo = linhas
    .filter((l) => l.status.codigo === 'so_2')
    .sort(ordenarPorMaxELNome);

  const semPremio = linhas
    .filter((l) => l.status.codigo === 'sem_premio')
    .sort(ordenarPorMaxELNome);

  return {
    ...barreiras,
    jogosSemResultado: jogosPendentes.length,
    disputaPrimeiro,
    soSegundo,
    semPremio,
  };
}
